import { Button, ColumnTable, Icon } from '@octano/global-ui';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { moneyFormatter } from '../../../../utils/currency';
import { formatDate } from '../../../../utils/dates';
import { PaymentHistoryRow } from '../types';

const dictPrefix = 'financialPortal.paymentHistory.table';
const NA = 'N/A';

interface Options {
  /**
   * Si false, oculta la columna `Estudiante` (uso individual donde el
   * contexto del alumno ya es implícito — HU-15469).
   */
  includeStudent?: boolean;
  onClickDetail: (row: PaymentHistoryRow) => void;
  onDownloadAttachment: (url: string) => void;
}

export function usePaymentHistoryColumns({
  includeStudent = true,
  onClickDetail,
  onDownloadAttachment,
}: Options): ColumnTable<PaymentHistoryRow>[] {
  const { t } = useTranslation();

  return useMemo<ColumnTable<PaymentHistoryRow>[]>(() => {
    const cols: ColumnTable<PaymentHistoryRow>[] = [];

    if (includeStudent) {
      cols.push({
        headerText: t(`${dictPrefix}.student`),
        columnName: 'studentName',
        tdClassName: 'text-center',
        thClassName: 'text-center',
        cellFormat: ({ row }) => (
          <div>
            <span className="d-block text-center">
              {row.studentName || '-'}
            </span>
            {row.studentIdentification ? (
              <span className="d-block text-center text-muted">
                {row.studentIdentification}
              </span>
            ) : null}
          </div>
        ),
      });
    }

    cols.push(
      {
        headerText: t(`${dictPrefix}.sustainer`),
        columnName: 'sustainerName',
        tdClassName: 'text-center',
        thClassName: 'text-center',
        // HU-15679: mostrar sustainer también para filas refund (operador que
        // aplicó la devolución, hidratado por backend desde
        // refundCharge.createdBy). Antes hacíamos short-circuit en
        // __source === 'refund' y se veía N/A — Jeison reportó la
        // inconsistencia vs el detalle de Acuerdo de Pago.
        cellFormat: ({ row }) =>
          !row.sustainerName ? (
            NA
          ) : (
            <div>
              <span className="d-block text-center">{row.sustainerName}</span>
              {row.sustainerIdentification ? (
                <span className="d-block text-center text-muted">
                  {row.sustainerIdentification}
                </span>
              ) : null}
            </div>
          ),
      },
      {
        headerText: t(`${dictPrefix}.paymentDate`),
        columnName: 'paymentDate',
        tdClassName: 'text-center',
        thClassName: 'text-center',
        cellFormat: ({ value }) =>
          value ? formatDate(value as string, 'DD/MM/YYYY') : '-',
      },
      {
        headerText: t(`${dictPrefix}.paymentType`),
        columnName: 'paymentType',
        tdClassName: 'text-center',
        thClassName: 'text-center',
        cellFormat: ({ row }) =>
          row.__source === 'refund' ? NA : row.paymentType ?? '-',
      },
      {
        headerText: t(`${dictPrefix}.documentType`),
        columnName: 'documentType',
        tdClassName: 'text-center',
        thClassName: 'text-center',
        cellFormat: ({ row }) =>
          row.__source === 'refund' ? NA : row.documentType ?? '-',
      },
      {
        headerText: t(`${dictPrefix}.voucherNumber`),
        columnName: 'voucherNumber',
        tdClassName: 'text-center',
        thClassName: 'text-center',
        cellFormat: ({ row }) =>
          row.__source === 'refund' ? NA : row.voucherNumber ?? '-',
      },
      {
        headerText: t(`${dictPrefix}.paymentGateway`),
        columnName: 'paymentGateway',
        tdClassName: 'text-center',
        thClassName: 'text-center',
        cellFormat: ({ row }) =>
          row.__source === 'refund' ? NA : row.paymentGateway ?? '-',
      },
      {
        headerText: t(`${dictPrefix}.authorizationCode`),
        columnName: 'authorizationCode',
        tdClassName: 'text-center',
        thClassName: 'text-center',
        cellFormat: ({ row }) =>
          row.__source === 'refund' ? NA : row.authorizationCode ?? '-',
      },
      {
        headerText: t(`${dictPrefix}.amount`),
        columnName: 'amount',
        tdClassName: 'text-right',
        thClassName: 'text-right',
        cellFormat: ({ row }) => {
          const isRefund = row.__source === 'refund';
          const formatted = moneyFormatter().format(Math.ceil(row.amount));
          return (
            <div
              className="d-inline-flex align-items-center"
              style={{ gap: 6 }}
            >
              <strong style={isRefund ? { color: '#9B1C1C' } : undefined}>
                {formatted}
              </strong>
              {isRefund && row.attachmentUrl ? (
                <span
                  role="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (row.attachmentUrl) {
                      onDownloadAttachment(row.attachmentUrl);
                    }
                  }}
                  title={t(`${dictPrefix}.downloadAttachment`)}
                >
                  <Icon name="download" size="16px" color="primary" />
                </span>
              ) : null}
            </div>
          );
        },
      },
      {
        headerText: t(`${dictPrefix}.action`),
        columnName: 'id',
        tdClassName: 'text-center',
        thClassName: 'text-center',
        width: 96,
        cellFormat: ({ row }) => (
          <Button
            size="sm"
            icon="eye"
            outlined
            text=""
            onClick={() => onClickDetail(row)}
          />
        ),
      },
    );
    return cols;
  }, [includeStudent, onClickDetail, onDownloadAttachment, t]);
}
